import { useEffect, useRef, useState } from "react"
import { convertDateTimeLocalToEpochMillis } from "popopo-lib-common"

export function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- React useEffect dependency array accepts any type for flexibility
export function usePromise<T>(src: () => Promise<T> | undefined, deps: ReadonlyArray<any> = []): T | "loading" | "error" {
  const [value, setValue] = useState<T | "error">()
  const loader = useRef<Promise<T>>()
  useEffect(() => {
    setValue(undefined)
    const promise = src()
    loader.current = promise
    loader.current
      ?.then((value) => {
        if (promise === loader.current) {
          setValue(value)
        }
      })
      .catch((e) => {
        console.error(e)
        setValue("error")
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  return value ?? "loading"
}

/** Date をローカルタイムゾーン基準で YYYY-MM-DD 文字列に変換する */
export function toDateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/** YYYY-MM-DD 文字列をローカルタイムゾーンの Date に変換する */
export function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number)
  return new Date(y, m - 1, d, 0, 0, 0, 0)
}

/** start〜end 間のすべての日付を Date[] で返す（両端含む） */
export function expandDateRange(start: Date, end: Date): Date[] {
  const dates: Date[] = []
  const cur = new Date(start)
  cur.setHours(0, 0, 0, 0)
  const endTime = new Date(end)
  endTime.setHours(0, 0, 0, 0)
  while (cur <= endTime) {
    dates.push(new Date(cur))
    cur.setDate(cur.getDate() + 1)
  }
  return dates
}

export const getTodayDateString = () => {
  return toDateString(new Date())
};

export const getTodayDateTimeLocalString = () => {
  const now = new Date();
  const off = now.getTimezoneOffset();
  const local = new Date(now.getTime() - off * 60 * 1000);
  return local.toISOString().slice(0,16); // "YYYY-MM-DDTHH:mm"
};

export const getDateTimeOrNow = (inputDateTime: string, isNow: boolean): string => {
  if (!inputDateTime) return getTodayDateTimeLocalString();

  const inputDate = convertDateTimeLocalToEpochMillis(inputDateTime);
  const now = convertDateTimeLocalToEpochMillis(getTodayDateTimeLocalString());

  return isNow && now < inputDate ? getTodayDateTimeLocalString() : inputDateTime;
};
